import React, { createContext, useContext, useEffect, useState } from 'react';
import usAllMapGeo from '@highcharts/map-collection/countries/us/us-all.geo.json';
import { getVendorsByUSStates } from 'utils/apiRequests';
import { USA_HEATMAP_FILTER_DATA } from './utils';
import { renderToString } from 'react-dom/server';
import {
	TooltipHeader,
	TooltipRowPinned,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './SupplierDiversity.styles';
import { formatNumber } from 'utils/number';
import { colors } from '@alixpartners/ui-components';

const AcrossUsa = createContext();

const intialChartOptions = {
	chart: {
		map: usAllMapGeo,
		style: { fontFamily: 'Roboto' },
		height: '27%',
	},
	exporting: { enabled: false, allowHTML: true },
	credits: { enabled: false },
	title: { text: '' },
	mapNavigation: { enabled: true },
	colorAxis: {
		// min: 1,
		// type: 'logarithmic',
		minColor: '#d2dadf',
		maxColor: '#1c475e',
		stops: [[0, '#d2dadf'], [0.67, '#77919e'], [1, '#1c475e']],
	},
	tooltip: {
		useHTML: true,
		backgroundColor: colors.white,
		borderWidth: 0,
		outside: true,
		borderRadius: 2,
		formatter: function() {
			const { point } = this;
			const { state_name, value } = point;
			return renderToString(
				<TooltipWrapper>
					<TooltipHeader>{state_name}</TooltipHeader>
					<TooltipRowPinned>
						<div />
						<TooltipRowTitle>Vendors</TooltipRowTitle>
						<TooltipRowValue>{formatNumber(value)}</TooltipRowValue>
					</TooltipRowPinned>
				</TooltipWrapper>
			);
		},
	},
	series: [
		{
			accessibility: {
				point: {
					valueDescriptionFormat: '{xDescription}, {point.value} vendors.',
				},
			},
			animation: { duration: 1000 },
			data: [],
			joinBy: ['postal-code', 'state_id'],
			dataLabels: {
				enabled: true,
				color: '#FFFFFF',
				format: '{point.state_id}',
			},
			name: 'Vendor density',
		},
	],
};

const calculateTotalVendorsByState = data => {
	const result = [];
	const totalVendorsDict = {};
	data.forEach(entry => {
		const stateId = entry.state_id;
		const uniqueVendors = entry.unique_vendors;
		const stateName = entry.state_name;

		if (totalVendorsDict[stateId]) {
			totalVendorsDict[stateId].total_vendors += uniqueVendors;
		} else {
			totalVendorsDict[stateId] = {
				state_name: stateName,
				total_vendors: uniqueVendors,
			};
		}
	});

	for (const stateId in totalVendorsDict) {
		result.push({
			state_id: stateId,
			state_name: totalVendorsDict[stateId].state_name,
			value: totalVendorsDict[stateId].total_vendors,
		});
	}
	return result;
};

export const AcrossUsaProvider = ({ children }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState(intialChartOptions);
	const [certificationFilter, setCertificationFilter] = useState(
		USA_HEATMAP_FILTER_DATA
	);

	useEffect(
		() => {
			getData();
		}, // eslint-disable-next-line
		[]
	);

	const getData = () => {
		setIsLoading(true);
		const payload = {
			certification_Flag: certificationFilter.selected,
		};
		getVendorsByUSStates(payload)
			.then(response => {
				if (response.succeeded) {
					const seriesData = calculateTotalVendorsByState(response.data);
					setData(seriesData);
					setOptions(prevState => ({
						...prevState,
						series: [
							{
								...prevState.series[0],
								data: seriesData,
							},
						],
					}));
					setIsLoading(false);
				} else {
					setIsLoading(false);
				}
			})
			.catch(() => setIsLoading(false));
	};

	const certificationFilterHandler = value =>
		setCertificationFilter(prevState => ({
			...prevState,
			selected: value,
		}));

	return (
		<AcrossUsa.Provider
			value={{
				isLoading,
				data,
				options,
				certificationFilter,
				certificationFilterHandler,
				getData,
			}}
		>
			{children}
		</AcrossUsa.Provider>
	);
};

export const useAcrossUsaContext = () => {
	return useContext(AcrossUsa);
};
